<template>
  <ul :class="$style.icons">
    <li v-if="userStore.isAuthenticated" :class="$style.accountMenu" @mouseenter="isShowMenuPc = true" @mouseleave="isShowMenuPc = false">
      <a href="/account" :class="$style.avatar" aria-label="マイページ" @click="onClickAvatar">
        <img v-if="userStore.picturePath" :src="dynamicImageUrl(userStore.picturePath, { width: 100, height: 100 })" alt="" width="32" height="32" />
        <span v-if="activityCount > 0" :class="$style.updated" />
      </a>
      <HeaderAccount :class="[$style.headerAccount, isShowMenuPc && $style.isShowMenuPc, isShowMenuSp && $style.isShowMenuSp]" />
    </li>
    <template v-else>
      <li>
        <a href="/sell" :class="[$style.text, $style.sell]">ショップをつくる</a>
      </li>
      <li>
        <a href="/signin" :class="$style.text" @click.prevent="onClickSignin">ログイン</a>
      </li>
    </template>
    <li v-if="userStore.isAuthenticated">
      <a href="/likes/item" :class="[$style.button, $style.favorite]" aria-label="お気に入り">
        <HeartSvg />
        <span v-if="timelineStore.isUpdated" :class="$style.updated" />
      </a>
    </li>
    <li>
      <a href="/cart" :class="[$style.button, $style.cart]" aria-label="カート">
        <CartSvg />
        <em v-if="cartStore.itemCount > 0" :class="$style.cartCount">{{ cartStore.itemCount }}</em>
      </a>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useMQ } from '@/js/lib/helper/media-query';
import { dynamicImageUrl } from '@/js/lib/helper/url';
import { useCartStore } from '@/js/lib/store/cart';
import { useNotificationStore } from '@/js/lib/store/notification';
import { useTimelineStore } from '@/js/lib/store/timeline';
import { useTodoStore } from '@/js/lib/store/todo';
import { useUserStore } from '@/js/lib/store/user';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import CartSvg from '@/svgs/vue/cart.svg';
import HeartSvg from '@/svgs/vue/heart.svg';
import HeaderAccount from './HeaderAccount.vue';

const userStore = useUserStore();
const notificationStore = useNotificationStore();
const todoStore = useTodoStore();
const timelineStore = useTimelineStore();
const cartStore = useCartStore();
const { isSp } = useMQ();
const isShowMenuPc = ref(false);
const isShowMenuSp = ref(false);
const activityCount = computed(() => todoStore.totalCount + notificationStore.updatedCount);
const onClickSignin = () => {
  signinSignup({
    active: 'signin',
    callback: () => {
      window.location.reload();
    },
  });
};
const onClickAvatar = (e: Event) => {
  if (isSp.value) {
    e.preventDefault();
  }
  isShowMenuSp.value = !isShowMenuSp.value;
};

onMounted(() => {
  cartStore.loadBaskets();
  if (userStore.isAuthenticated) {
    notificationStore.loadNotifications();
    todoStore.loadTodos();
  }
});
</script>

<style module lang="scss">
@keyframes account-menu-fade-in {
  0% {
    display: none;
    opacity: 0;
  }

  1% {
    display: block;
    opacity: 0;
  }

  100% {
    display: block;
    opacity: 1;
  }
}

.icons {
  align-items: center;
  display: flex;
  grid-area: icons;
  justify-self: flex-end;

  > li {
    margin: 0 2px;
    position: relative;
    white-space: nowrap;

    @media (--sp) {
      margin: 0;
      position: static;
    }

    &:first-child {
      margin-left: 0;
    }

    &.accountMenu {
      margin-right: 4px;

      @media (--sp) {
        margin-right: 8px;
      }

      .avatar {
        cursor: pointer;
        display: block;
        padding: 0 8px 8px;
        position: relative;

        @media (--sp) {
          padding: 0;
        }

        img {
          border-radius: 15px;
          display: block;
          pointer-events: none;
        }

        .updated {
          background-color: var(--color-red);
          border-radius: 6px;
          height: 12px;
          position: absolute;
          right: 6px;
          top: 1px;
          width: 12px;

          @media (--sp) {
            right: -2px;
          }
        }
      }
    }
  }
}

.text {
  display: block;
  margin: 0 2px 8px 0;
  padding: 0 8px;

  @media (--sp) {
    margin-bottom: 0;
  }

  &.sell {
    @media (--sp) {
      display: none;
    }
  }
}

.button {
  display: block;
  height: 36px;
  margin-bottom: 8px;
  position: relative;
  width: 42px;

  @media (--sp) {
    margin-bottom: 0;
  }

  > svg {
    margin: -3px 0;
    width: 100%;
  }

  &.favorite {
    .updated {
      background-color: var(--color-red);
      border-radius: 6px;
      height: 12px;
      position: absolute;
      right: 3px;
      top: 3px;
      width: 12px;
    }
  }

  &.cart {
    .cartCount {
      background-color: var(--color-red);
      border-radius: 50%;
      color: var(--color-white);
      display: block;
      font-size: 10px;
      font-style: normal;
      font-weight: 700;
      height: 14px;
      line-height: 14px;
      min-width: 14px;
      position: absolute;
      right: 0;
      text-align: center;
      top: 3px;
    }
  }
}

.headerAccount {
  display: none;
  opacity: 0;

  &.isShowMenuPc {
    @media (--pc) {
      animation: account-menu-fade-in 0.15s ease-in 0s forwards;
      display: block;
    }
  }

  &.isShowMenuSp {
    @media (--sp) {
      animation: account-menu-fade-in 0.15s ease-in 0s forwards;
      display: block;
    }
  }
}
</style>
